import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';

import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CUSTOM_PERIOD_VALUE } from '@/pages/ManagerAssessment/Evaluation/components/EvaluationDatePeriod';

export const CycleInformationFormFields = {
    name: 'name',
    selectedPeriod: 'selectedPeriod',
    periodStart: 'periodStart',
    periodEnd: 'periodEnd',
    audienceType: 'audienceType',
    resolvedEmployeeCount: 'resolvedEmployeeCount',
} as const;

export const CYCLE_NAME_MAX_LENGTH = 100;

export const NAME_REQUIRED: TranslationKey =
    'cycleEvaluationCycleInformation.validation.nameRequired';
export const NAME_TOO_LONG: TranslationKey =
    'cycleEvaluationCycleInformation.validation.nameTooLong';
const PERIOD_REQUIRED: TranslationKey = 'cycleEvaluationCycleInformation.validation.periodRequired';
const PERIOD_END_BEFORE_START: TranslationKey =
    'cycleEvaluationCycleInformation.validation.periodEndBeforeStart';
const NO_AUDIENCE: TranslationKey = 'cycleEvaluationCycleInformation.validation.noAudience';

const audienceTypeSchema = z.enum([
    CycleAudienceType.ENTIRE_COMPANY,
    CycleAudienceType.FILTER_BY_ATTRIBUTES,
]);

interface CompletePeriodCheck {
    selectedPeriod: string;
    periodStart?: Date;
    periodEnd?: Date;
    offeredPeriodValues: readonly string[];
}

/**
 * A saved period the server no longer offers renders as an empty selector, and completing the step
 * in that state writes three nulls over the cycle's own dates.
 */
export const hasCompletePeriod = ({
    selectedPeriod,
    periodStart,
    periodEnd,
    offeredPeriodValues,
}: CompletePeriodCheck): boolean => {
    if (selectedPeriod === CUSTOM_PERIOD_VALUE) {
        return Boolean(periodStart && periodEnd);
    }

    return selectedPeriod.length > 0 && offeredPeriodValues.includes(selectedPeriod);
};

const hasOrderedPeriod = ({
    periodStart,
    periodEnd,
}: Pick<CompletePeriodCheck, 'periodStart' | 'periodEnd'>): boolean => {
    if (!periodStart || !periodEnd) {
        return true;
    }

    return periodEnd.getTime() >= periodStart.getTime();
};

interface StepCompletenessCheck extends CompletePeriodCheck {
    name: string;
}

export const isCycleInformationComplete = ({
    name,
    selectedPeriod,
    periodStart,
    periodEnd,
    offeredPeriodValues,
}: StepCompletenessCheck): boolean =>
    name.trim().length > 0 &&
    hasCompletePeriod({ selectedPeriod, periodStart, periodEnd, offeredPeriodValues });

export const buildCycleInformationSchema = (offeredPeriodValues: readonly string[]) =>
    z
        .object({
            name: z.string().trim().min(1, NAME_REQUIRED).max(CYCLE_NAME_MAX_LENGTH, NAME_TOO_LONG),
            selectedPeriod: z.string(),
            periodStart: z.date().optional(),
            periodEnd: z.date().optional(),
            audienceType: audienceTypeSchema,
            resolvedEmployeeCount: z.number(),
        })
        .refine((values) => hasCompletePeriod({ ...values, offeredPeriodValues }), {
            message: PERIOD_REQUIRED,
            path: [CycleInformationFormFields.selectedPeriod],
        })
        .refine(hasOrderedPeriod, {
            message: PERIOD_END_BEFORE_START,
            path: [CycleInformationFormFields.selectedPeriod],
        })
        .refine(({ resolvedEmployeeCount }) => resolvedEmployeeCount > 0, {
            message: NO_AUDIENCE,
            path: [CycleInformationFormFields.resolvedEmployeeCount],
        });

export type CycleInformationFormData = z.infer<ReturnType<typeof buildCycleInformationSchema>>;
